'use client'
// DealCard — renders a single flight deal; all data via props, no direct imports
import { useState, useEffect } from "react";
import dynamic from 'next/dynamic';
const DealCalendarModal = dynamic(() => import('./DealCalendarModal'), { ssr: false })
import { useRouter } from 'next/navigation';
import Link from "next/link";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { AIRPORT_DB, FLAG_COUNTRY } from "./AirportCombobox";
import { isSeasonActive } from "@/data/deals";
import AIRLINE_DB from "@/data/airlines.json";
import { routeSlug, dealSlug, DURATION_TO_SLUG } from '@/lib/dealSlug';

function airlineSlug(name) {
  if (!name) return null
  const nl = name.toLowerCase()
  for (const a of Object.values(AIRLINE_DB)) {
    const al = a.name.toLowerCase()
    if (al === nl || al.startsWith(nl + ' ')) return a.slug
  }
  return null
}

function AirlineLink({ name, className }) {
  const slug = airlineSlug(name)
  if (slug) return <Link href={`/airlines/${slug}`} onClick={(e) => e.stopPropagation()} className={className}>{name}</Link>
  return <span>{name}</span>
}
import { trackEvent } from '@/lib/trackEvent'
import DESTINATIONS_DB from "@/data/destinations-client.json";
import { buildGoogleFlightsUrl } from "@/lib/googleFlights.js";
import { buildAirlineBookingUrl, buildSkyscannerUrl, buildKayakUrl, buildMomondoUrl, buildKiwiUrl, buildTripUrl } from "@/lib/airlineBooking.js";
import ShareMenu from "./ShareMenu";
import { useAuth } from "@/context/AuthContext";
import { getUserProfile } from '@/lib/userProfile';
import { useCurrency, currencySymbol } from "@/lib/currency";

// PriceSparkline — draws a pure-SVG trend line from a pre-fetched prices array.
// Green = trending down (good), red = trending up (bad). No external libs.
// prices===null means still loading; prices=[] means no data.
// locked=true (free tier) shows an upgrade prompt instead of the actual chart.
function PriceSparkline({ prices, locked }) {
  const t = useTranslations("dealCard");
  if (locked) return null;
  if (!prices || prices.length < 2) return null;

  const pts = prices.slice(-30);
  const n   = pts.length;
  const min = Math.min(...pts);
  const max = Math.max(...pts);
  const W = 120, H = 32;
  const trendingUp = pts[n - 1] > pts[0];
  const color = trendingUp ? "#ef4444" : "#22c55e";
  const toY = (p) => max === min ? H / 2 : H - ((p - min) / (max - min)) * H;
  const d = pts
    .map((p, i) => `${i === 0 ? "M" : "L"}${((i / (n - 1)) * W).toFixed(1)},${toY(p).toFixed(1)}`)
    .join(" ");

  return (
    <div className="mt-2 flex items-center gap-2">
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
        <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span className="text-xs" style={{ color }}>
        {trendingUp ? t("priceRising") : t("priceDropping")}
      </span>
    </div>
  );
}

// Price-tier badge — thresholds scale with flight duration (same logic as fetch_explore absThreshold).
// cap: ≤3h → €99, 3-6h → €179, >6h → 449 × (mins/480) ≈ €448 NYC, €784 Sydney, €1235 Perth.
// Tiers: red = ≤20% of cap (absurd), orange = ≤50% (very good), green = ≤100% (good deal price).
// Returns {label, bg} only for truly exceptional prices with no discount.
// Green tier omitted — a normal deal price doesn't warrant a standalone badge.
function priceTier(price, totalDuration, dealCategory) {
  const mins = totalDuration ?? (dealCategory === 'long-haul' ? 480 : dealCategory === 'mid-haul' ? 270 : 120)
  const cap  = mins <= 180 ? 99 : mins <= 360 ? 179 : Math.round(449 * mins / 480)
  if (!price || price > cap * 0.60) return null
  const [t1, t2] = mins > 360 ? [0.33, 0.60] : [0.20, 0.50]
  if (price <= cap * t1) return { label: 'Crazy price', bg: 'bg-red-500' }
  if (price <= cap * t2) return { label: 'Act fast',    bg: 'bg-orange-500' }
  return null
}

// ScoreBadge — colored circle + label. Gold 9-10, green 7-8, blue 5-6, gray <5.
function ScoreBadge({ score, label, breakdown }) {
  if (score == null) return null;

  const { bg, textColor } =
    score >= 9
      ? { bg: "#b45309", textColor: "#fff" }   // gold (amber-700)
      : score >= 7
      ? { bg: "#16a34a", textColor: "#fff" }   // green-600
      : score >= 5
      ? { bg: "#2563eb", textColor: "#fff" }   // blue-600
      : { bg: "#9ca3af", textColor: "#fff" };  // gray-400

  const tooltipParts = breakdown
    ? [
        `Deal score: ${score}/10`,
        "",
        `Price quality:  ${breakdown.median_discount?.toFixed(1) ?? "–"}`,
        `Popularity:        ${breakdown.popularity?.toFixed(1) ?? "–"}`,
        `Seasonality:       ${breakdown.seasonality?.toFixed(1) ?? "–"}`,
        `Timing (days out): ${breakdown.days_out?.toFixed(1) ?? "–"}`,
        `Days until flight: ${breakdown.days_until_departure ?? "–"}`,
      ]
    : [`Deal score: ${score}/10`];

  return (
    <div
      className="flex items-center gap-1.5 cursor-help"
      title={tooltipParts.join("\n")}
    >
      <span
        className="inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold shadow-sm shrink-0"
        style={{ backgroundColor: bg, color: textColor }}
      >
        {score % 1 === 0 ? score : score.toFixed(1)}
      </span>
      <span className="text-xs font-semibold" style={{ color: bg }}>
        {label}
      </span>
    </div>
  );
}

function formatDate(dateStr) {
  // Parse as local date to avoid UTC-offset shifting the day
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatDateRange(startStr, endStr, count) {
  const [sy, sm] = startStr.split("-").map(Number);
  const [ey, em] = endStr.split("-").map(Number);
  const startLabel = new Date(sy, sm - 1, 1).toLocaleDateString("en-GB", { month: "short" });
  const endLabel   = new Date(ey, em - 1, 1).toLocaleDateString("en-GB", { month: "short" });
  if (sm === em && sy === ey) {
    return `Multiple dates in ${startLabel} (${count})`;
  }
  return `${startLabel} — ${endLabel} (${count} dates)`;
}

function formatDuration(minutes) {
  if (minutes == null) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// ─── Error fare urgency indicator (countdown if future expiry, else static) ──
function ErrorFareUrgency({ expiresAt }) {
  const [msLeft, setMsLeft] = useState(null)
  useEffect(() => {
    if (!expiresAt) return
    const target = new Date(expiresAt).getTime()
    const update = () => setMsLeft(Math.max(0, target - Date.now()))
    update()
    if (target > Date.now()) {
      const id = setInterval(update, 1000)
      return () => clearInterval(id)
    }
  }, [expiresAt])
  if (msLeft !== null && msLeft > 0) {
    const h = Math.floor(msLeft / 3600000)
    const m = Math.floor((msLeft % 3600000) / 60000)
    const s = Math.floor((msLeft % 60000) / 1000)
    return <span className="text-[11px] font-bold text-white/90">Expires in {h > 0 ? `${h}h ` : ''}{m}m {s}s</span>
  }
  return <span className="text-[11px] font-bold text-white/90">Likely to disappear — book now</span>
}

// ─── Deal type badge ─────────────────────────────────────────────────────────
function DealTypeBadge({ type }) {
  if (!type || type === 'return') return null;
  const config = {
    one_way:      { label: 'One-way',       bg: 'bg-purple-100', text: 'text-purple-700' },
    mixed_carrier:{ label: 'Mixed-carrier', bg: 'bg-orange-100', text: 'text-orange-700' },
    open_jaw:     { label: 'Open-jaw',      bg: 'bg-teal-100',   text: 'text-teal-700'   },
  };
  const c = config[type];
  if (!c) return null;
  return (
    <span className={`${c.bg} ${c.text} text-xs font-semibold px-2 py-0.5 rounded-full`}>
      {c.label}
    </span>
  );
}

// ─── Payment type badge (cash | award | error_fare) ──────────────────────────
function PaymentTypeBadge({ dealType }) {
  if (dealType === 'error_fare') {
    return (
      <span className="bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
        ⚡ Error Fare
      </span>
    );
  }
  if (dealType === 'award') {
    return (
      <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded-full">
        ✈️ Award
      </span>
    );
  }
  if (dealType === 'cash') {
    return (
      <span className="bg-emerald-50 text-emerald-700 text-xs font-medium px-2 py-0.5 rounded-full">
        💰 Cash
      </span>
    );
  }
  return null;
}

// ─── Deal quality badge ───────────────────────────────────────────────────────
// Thresholds: >20% below avg = Good, >35% = Great, >50% = Exceptional
// tier='free' → hidden; tier='plus' → badge only; tier='pro' → badge + savings line
function DealQualityBadge({ discountPct, deal_type, normalPrice, price, formatDealPrice, tier }) {
  // Text label = our judgment, based on deal_type from the fetcher.
  // Only shown when there is no reliable percentage to show (abs-only classification).
  // When discountPct >= 10, the colored badge next to the price already communicates quality.
  const hasReliablePct = discountPct != null && discountPct >= 10;
  if (hasReliablePct) return null; // percentage badge handles it

  let label, emoji, bg, text;
  if (deal_type === 'error_fare' || deal_type === 'exceptional') {
    label = 'Exceptional price'; emoji = '🔥'; bg = 'bg-amber-100'; text = 'text-amber-700';
  } else if (deal_type === 'deal') {
    label = 'Great value'; emoji = '⭐'; bg = 'bg-orange-50'; text = 'text-orange-600';
  } else if (deal_type === 'good_price') {
    label = 'Good price'; emoji = '✓'; bg = 'bg-green-50'; text = 'text-green-600';
  } else {
    return null;
  }
  return (
    <div className="mt-1.5 flex items-center gap-2 flex-wrap">
      <span className={`${bg} ${text} text-xs font-semibold px-2 py-0.5 rounded-full`}>
        {emoji} {label}
      </span>
    </div>
  );
}

// ─── Baggage/interline warnings ───────────────────────────────────────────────
function BaggageWarning({ interline, baggage }) {
  if (!interline && !baggage) return null;
  const msg = interline
    ? '⚠ Interline: bagage opnieuw inchecken — controleer voorwaarden per maatschappij.'
    : '⚠ Bagage-voorwaarden kunnen verschillen per vlucht — controleer voor boeking.';
  return (
    <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
      {msg}
    </div>
  );
}

const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const MONTH_INITIALS = ['J','F','M','A','M','J','J','A','S','O','N','D']

function MonthBar({ departure_months, departure_dates, formatDealPrice, currentUser, tier, airlineLogo, carryOnIncluded, destSlug, legs, layovers }) {
  const router = useRouter()
  const [selectedDealId, setSelectedDealId] = useState(null)
  useEffect(() => {
    setSelectedDealId(new URLSearchParams(window.location.search).get('deal'))
  }, [])
  const fmtTime = t => t ? t.toString().slice(-5) : null
  const fmtDur = m => m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ''}` : `${m}m`
  const depTime = legs?.[0]?.departure ? fmtTime(legs[0].departure) : null
  const arrTime = legs?.length > 0 ? fmtTime(legs[legs.length - 1]?.arrival) : null
  const hasTime = !!depTime
  const cheapestMonth = (() => {
    if (!departure_dates?.length) return null
    const cheapest = departure_dates.reduce((best, d) => (!best || (d.price ?? Infinity) < (best.price ?? Infinity)) ? d : best, null)
    if (!cheapest?.date) return null
    return parseInt(cheapest.date.split('-')[1], 10)
  })()

  const [openMonth, setOpenMonth] = useState(cheapestMonth)
  const [showAllDates, setShowAllDates] = useState(false)
  const isGuest = !currentUser
  const currentMonth = new Date().getMonth() + 1

  const toggle = (e, m) => {
    e.stopPropagation()
    e.preventDefault()
    setOpenMonth(prev => { if (prev !== m) setShowAllDates(false); return prev === m ? null : m })
  }

  const datesForMonth = m => (departure_dates ?? []).filter(d => parseInt(d.date.split('-')[1], 10) === m).sort((a, b) => a.date < b.date ? -1 : a.date > b.date ? 1 : (a.returnDate ?? '') < (b.returnDate ?? '') ? -1 : (a.returnDate ?? '') > (b.returnDate ?? '') ? 1 : (a.price ?? 0) - (b.price ?? 0))

  // Cheapest price per month for the price tag below each button
  const minPriceByMonth = {}
  if (departure_dates?.length) {
    for (const d of departure_dates) {
      if (!d.date) continue
      const mn = parseInt(d.date.split('-')[1], 10)
      if (minPriceByMonth[mn] == null || (d.price ?? Infinity) < minPriceByMonth[mn]) {
        minPriceByMonth[mn] = d.price
      }
    }
  }

  return (
    <div className="mt-2" onClick={e => { e.stopPropagation(); e.preventDefault() }}>
      <div className="flex items-end gap-0.5">
        {MONTH_INITIALS.map((initial, i) => {
          const m = i + 1
          const active = departure_months?.includes(m)
          const open = openMonth === m
          const isCurrent = m === currentMonth
          const monthPrice = active && minPriceByMonth[m] != null ? formatDealPrice(minPriceByMonth[m]).primary : null
          return (
            <button
              key={i}
              type="button"
              disabled={!active}
              onClick={active ? e => toggle(e, m) : undefined}
              className={[
                'flex flex-col items-center justify-end flex-1 min-w-0 min-h-[44px] pb-0.5 pt-1 rounded transition-colors',
                open
                  ? 'bg-blue-600 text-white'
                  : active
                    ? `text-blue-600 hover:bg-blue-50 active:bg-blue-100${isCurrent ? ' border-b-2 border-blue-400' : ''}`
                    : isCurrent
                      ? 'text-gray-500 border-b-2 border-gray-400 cursor-default'
                      : 'text-gray-200 cursor-default',
              ].join(' ')}
            >
              {monthPrice && (
                <span className={`text-[7px] font-semibold leading-tight tabular-nums truncate w-full text-center ${open ? 'text-white/90' : 'text-blue-500'}`}>
                  {monthPrice}
                </span>
              )}
              <span className="text-[10px] font-bold leading-tight">{initial}</span>
            </button>
          )
        })}
      </div>

      {openMonth !== null && (() => {
        const allDates = datesForMonth(openMonth)
        const isPaid = !isGuest && tier !== 'free'
        const maxVisible = isGuest ? 1 : 3
        const visibleDates = (isPaid && showAllDates) ? allDates : allDates.slice(0, maxVisible)
        const hiddenCount = Math.max(0, allDates.length - visibleDates.length)
        const upgradeHref = isGuest ? '/signup' : '/pricing'
        const upgradeLabel = isGuest
          ? `🔓 ${hiddenCount} more date${hiddenCount !== 1 ? 's' : ''} — sign up free →`
          : `🔓 ${hiddenCount} more date${hiddenCount !== 1 ? 's' : ''} — upgrade to Plus →`
        return (
          <div className="mt-1.5 rounded-xl border border-gray-100 bg-gray-50 overflow-hidden">
            {visibleDates.map(({ date, returnDate, price, id, airline, stops }) => {
              const [dy, dm, dd] = date.split('-').map(Number)
              const depLabel = `${dd} ${MONTH_LABELS[dm - 1]}`
              let retLabel = null, nights = null
              if (returnDate) {
                const [ry, rm, rd] = returnDate.split('-').map(Number)
                nights = Math.round((new Date(ry, rm - 1, rd) - new Date(dy, dm - 1, dd)) / 86400000)
                retLabel = `${rd} ${MONTH_LABELS[rm - 1]}`
              }
              const dateHref = destSlug ? `/destinations/${destSlug}?deal=${id}` : `/deals/${id}`
              return (
                <button
                  key={id}
                  type="button"
                  onClick={e => { e.stopPropagation(); setSelectedDealId(id); router.push(dateHref, { scroll: false }) }}
                  className={`flex flex-col w-full text-left px-3 py-2 transition-colors gap-0.5 border-b border-gray-100 last:border-0 ${selectedDealId === id ? 'bg-blue-50 border-l-2 border-l-blue-400' : 'hover:bg-blue-50 active:bg-blue-100'}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-gray-700 min-w-0">
                      {depLabel}{retLabel && <span className="text-gray-500"> – {retLabel}</span>}
                      {nights != null && <span className="text-gray-500"> · {nights}n</span>}
                    </span>
                    <span className="text-xs font-semibold text-blue-600 shrink-0">{formatDealPrice(price).primary} →</span>
                  </div>
                  {(airline || stops != null || carryOnIncluded != null || hasTime) && (
                    <div className="flex items-center gap-1 flex-wrap mt-0.5">
                      {airlineLogo && (
                        <Image src={airlineLogo} alt="" width={12} height={12} className="h-3 w-3 object-contain" onError={e => { e.target.style.display = 'none' }} />
                      )}
                      {airline && <span className="text-[11px] text-gray-500">{airline}</span>}
                      {hasTime && (
                        <span className="text-[11px] text-gray-500 font-medium">
                          🕐 {depTime}{arrTime && arrTime !== depTime ? ` → ${arrTime}` : ''}
                        </span>
                      )}
                      {stops != null && (
                        <span className={`text-[10px] px-1.5 py-0 rounded-full font-medium leading-4 ${
                          stops === 0 ? 'bg-green-100 text-green-700' : stops === 1 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {stops === 0 ? 'Direct' : stops === 1 ? '1 stop' : `${stops} stops`}
                        </span>
                      )}
                      {layovers?.length > 0 && (
                        <span className="text-[11px] text-gray-500">
                          via {layovers.map((l, i) => (
                            <span key={i}>{i > 0 ? ', ' : ''}{l.airport}{l.duration ? ` (${fmtDur(l.duration)})` : ''}{l.overnight ? ' 🌙' : ''}</span>
                          ))}
                        </span>
                      )}
                      {carryOnIncluded != null && (
                        <span className={`text-[10px] px-1.5 py-0 rounded-full font-medium leading-4 ${
                          carryOnIncluded ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {carryOnIncluded ? '🎒 Carry-on ✓' : '🎒 No carry-on'}
                        </span>
                      )}
                    </div>
                  )}
                </button>
              )
            })}
            {hiddenCount > 0 && (
              isPaid ? (
                <button
                  onClick={e => { e.stopPropagation(); e.preventDefault(); setShowAllDates(true) }}
                  className="flex items-center justify-center w-full px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors border-t border-gray-100"
                >
                  <span className="text-xs font-medium text-gray-500">Show {hiddenCount} more date{hiddenCount !== 1 ? 's' : ''} ↓</span>
                </button>
              ) : (
                <Link
                  href={upgradeHref}
                  onClick={e => e.stopPropagation()}
                  className="flex items-center justify-center px-3 py-2.5 bg-blue-50 hover:bg-blue-100 transition-colors border-t border-blue-100"
                >
                  <span className="text-xs font-semibold text-blue-700">{upgradeLabel}</span>
                </Link>
              )
            )}
          </div>
        )
      })()}
    </div>
  )
}

function trackClick({ dealId, origin, destination, price, discountPct, tier }) {
  if (typeof window === 'undefined') return
  try {
    fetch('/api/track-click', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        dealId, origin, destination, price, discountPct,
        userTier: tier ?? 'free',
        sourcePage: window.location.pathname,
      }),
      keepalive: true,
    }).catch(() => {})
  } catch {}
}

export default function DealCard({ deal, isFlexMatch = false, sparklinePrices = [], lastVisit = null, tier = 'free', hidePhoto = false, showGoogleFlights = false, locked = false, priceWatchMatch = false, wishlist = [], showGradient = false, homeAirports = [], isDealOfHour = false, aboveFold = false }) {
  const t = useTranslations("dealCard");
  const [logoError, setLogoError] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false)
  const [shareOpen, setShareOpen] = useState(false);
  const [reportState, setReportState] = useState(null); // null | "loading" | "expired" | "still_valid"

  // Close share popover on outside click
  useEffect(() => {
    if (!shareOpen) return;
    function handleDocClick() { setShareOpen(false); }
    document.addEventListener('click', handleDocClick);
    return () => document.removeEventListener('click', handleDocClick);
  }, [shareOpen]);

  const { formatDealPrice, currency } = useCurrency();
  const { currentUser } = useAuth()
  const [resolvedTier, setResolvedTier] = useState(tier)
  useEffect(() => {
    if (!currentUser?.uid || tier !== 'free') { setResolvedTier(tier); return }
    getUserProfile(currentUser.uid).then(p => { if (p?.tier) setResolvedTier(p.tier) })
  }, [currentUser?.uid, tier])
  const refCode = currentUser?.uid ? currentUser.uid.replace(/-/g, '').slice(0, 8).toLowerCase() : null

  // City name lookup — falls back to IATA code if unknown
  const originCity    = AIRPORT_DB[deal.origin]?.city ?? deal.origin;
  const destCity      = DESTINATIONS_DB[deal.destination]?.city_name ?? AIRPORT_DB[deal.destination]?.city ?? deal.destination;
  const originFlag    = AIRPORT_DB[deal.origin]?.flag ?? '';
  const destFlag      = AIRPORT_DB[deal.destination]?.flag ?? '';
  const originCountry = FLAG_COUNTRY[originFlag] ?? '';
  const destCountry   = FLAG_COUNTRY[destFlag] ?? '';

  // Destination photo — from destinations.json keyed by IATA code
  const destPhotoUrl = DESTINATIONS_DB[deal.destination]?.image_url ?? null;
  // Fallback gradient: deterministic color from IATA code chars
  const FALLBACK_GRADIENTS = [
    ['#60a5fa', '#4f46e5'], // blue → indigo
    ['#34d399', '#0d9488'], // emerald → teal
    ['#f97316', '#e11d48'], // orange → rose
    ['#a78bfa', '#7c3aed'], // violet → purple
    ['#38bdf8', '#06b6d4'], // sky → cyan
    ['#fbbf24', '#ea580c'], // amber → orange
  ];
  const gradientIdx = deal.destination.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % FALLBACK_GRADIENTS.length;
  const [gradFrom, gradTo] = FALLBACK_GRADIENTS[gradientIdx];

  function handleShareClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/deals/${deal.id}${refCode ? `?ref=${refCode}` : ''}`;
    const text = `✈️ ${originCity} → ${destCity} from ${currencySymbol(deal.currency)}${deal.price} (-${deal.discountPct}%) on FairFares`;
    if (navigator.share) {
      navigator.share({ title: `${originCity} → ${destCity} from ${currencySymbol(deal.currency)}${deal.price}`, text, url })
        .then(() => window.plausible?.('ShareDeal', { props: { route: `${deal.origin}-${deal.destination}`, method: 'native' } }))
        .catch(() => {});
      return;
    }
    setShareOpen((o) => !o);
  }

  async function handleReportExpired(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!currentUser || reportState === 'loading') return;
    setReportState('loading');
    try {
      const token = localStorage.getItem('fairfares_session');
      const res = await fetch('/api/report-deal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          deal_id: deal.id,
          route_key: `${deal.origin}-${deal.destination}-${deal.travelDuration}`,
          origin: deal.origin,
          destination: deal.destination,
          travel_duration: deal.travelDuration,
          normal_price: deal.normalPrice,
        }),
      });
      const data = await res.json();
      setReportState(data.scan_result === 'still_valid' ? 'still_valid' : 'expired');
    } catch {
      setReportState('expired');
    }
  }

  // Guard: locked deals only carry {id, destination, price, discountPct, _locked}.
  // Early return before destructuring avoids TypeError on undefined fields during SSR.
  if (locked) {
    const { primary: pricePrimary } = formatDealPrice(deal.price)
    const _isProOnly = deal._proOnly
    return (
      <Link href={_isProOnly ? '/pricing' : '/signup'} className="block group h-full">
        <div className="relative bg-white rounded-2xl border border-gray-100 w-full h-full overflow-hidden shadow-sm">
          {!hidePhoto && (
            <div className="relative aspect-[4/3] w-full">
              {destPhotoUrl
                ? <Image src={destPhotoUrl} alt="" fill {...(aboveFold ? { priority: true } : { loading: "lazy" })} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw" className="object-cover" />
                : <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${gradFrom}, ${gradTo})` }} />}
            </div>
          )}
          <div className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-base sm:text-xl font-bold text-gray-900 leading-tight truncate">{destFlag} {destCity}</div>
                <div className="h-3 w-32 bg-gray-100 rounded mt-1.5" />
              </div>
              <div className="flex flex-col items-end gap-1.5 shrink-0">
                <div className="h-5 w-16 bg-gray-100 rounded-full" />
                <div className="h-5 w-12 bg-gray-100 rounded-full" />
              </div>
            </div>
            <div className="mt-3">
              <div className="text-2xl font-bold text-gray-900">{pricePrimary}</div>
              <div className="h-3 w-24 bg-gray-100 rounded mt-1.5" />
            </div>
            <div className="mt-2 h-8 w-[122px] bg-gray-100 rounded" />
            <div className="mt-2 flex items-center gap-2">
              <div className="h-3 w-20 bg-gray-100 rounded" />
              <div className="h-5 w-14 bg-gray-100 rounded-full" />
            </div>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
              <div className="h-3 w-36 bg-gray-100 rounded" />
              <div className="h-3 w-24 bg-gray-100 rounded" />
            </div>
            <div className="mt-2 h-3 w-28 bg-gray-100 rounded" />
            <div className="mt-4 flex items-center justify-between">
              <div className="h-5 w-16 bg-gray-100 rounded" />
              <div className="h-7 w-20 bg-gray-100 rounded-lg" />
            </div>
          </div>
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-10">
            <span className="text-gray-900 text-sm font-bold bg-white shadow px-4 py-2 rounded-full group-hover:shadow-md transition-shadow">{_isProOnly ? '✈️ Pro deal →' : '🔒 Unlock →'}</span>
          </div>
        </div>
      </Link>
    )
  }

  const {
    id,
    type,
    deal_type,
    origin,
    destination,
    price,
    normalPrice,
    discountPct,
    airline,
    airlineLogo,
    outbound_airline,
    inbound_airline,
    outbound_airline_logo,
    inbound_airline_logo,
    open_jaw_return_origin,
    departureDate,
    returnDate,
    totalDuration,
    stops,
    baseline_confidence,
    baseline_source,
    score,
    score_label,
    score_breakdown,
    expired_at,
    created_at,
    seasons,
    baggage_warning,
    interline_warning,
    date_count,
    price_from,
    departure_months,
    departure_dates,
    date_range_start,
    date_range_end,
    award_price_miles,
    fetched_at,
    updated_at,
    co2_kg,
    typicalPriceRange,
    priceByMonth,
    deal_category,
    discount_source,
    travelDuration,
    booking_url,
    airlineCode,
    legs,
    layovers,
  } = deal;

  const isOneWay = type === 'one_way' || !returnDate;
  const isMixedOrOpenJaw = type === 'mixed_carrier' || type === 'open_jaw';

  const seasonActive = isSeasonActive(seasons);
  const showSeasonBadge = seasons && seasons !== 'year-round';

  const isNew = (() => {
    if (!created_at) return false;
    const createdMs = new Date(created_at).getTime();
    if (isNaN(createdMs)) return false;
    const threshold = lastVisit != null
      ? Math.max(lastVisit, Date.now() - 86400000)
      : Date.now() - 86400000;
    return createdMs > threshold;
  })();

  const expiredHoursAgo = expired_at
    ? Math.max(0, Math.round((Date.now() - new Date(expired_at).getTime()) / 3600000))
    : null;

  const isLowConfidence =
    baseline_confidence === "low" || (baseline_source != null && baseline_source !== "own");

  const isTrendingUp =
    sparklinePrices && sparklinePrices.length >= 2 &&
    sparklinePrices[sparklinePrices.length - 1] > sparklinePrices[0];

  const [dy, dm, dd] = departureDate.split("-").map(Number);
  const nights = returnDate
    ? (() => {
        const [ry, rm, rd] = returnDate.split("-").map(Number);
        return Math.round((new Date(ry, rm - 1, rd) - new Date(dy, dm - 1, dd)) / 86400000);
      })()
    : null;

  // Carry-on lookup: extract IATA code from logo URL → look up in AIRLINE_DB
  const logoForIata = airlineLogo || outbound_airline_logo || '';
  const airlineIata = logoForIata.match(/\/70px\/([A-Z0-9]+)\.png/i)?.[1]?.toLowerCase() ?? null;
  const carryOnIncluded = airlineIata != null && AIRLINE_DB[airlineIata]
    ? (AIRLINE_DB[airlineIata].baggage_carry_on ?? null)
    : null;

  // Deal freshness: hours since created_at (DB deals) or fetched_at (explore deals)
  const hoursAgo = (created_at || fetched_at)
    ? Math.max(0, Math.round((Date.now() - new Date(created_at || fetched_at).getTime()) / 3600000))
    : null;

  const isMultiDate = (departure_dates?.length > 1) || (date_count > 1)
  const photoMinPrice = isMultiDate && departure_dates?.length > 1
    ? Math.min(...departure_dates.map(d => d.price ?? price))
    : price
  const photoPriceLabel = formatDealPrice(photoMinPrice).primary
  const updatedHoursAgo = updated_at
    ? Math.max(0, Math.round((Date.now() - new Date(updated_at).getTime()) / 3_600_000))
    : null
  const primaryHoursAgo = updatedHoursAgo ?? hoursAgo
  const photoTimeLabel = primaryHoursAgo == null ? null
    : primaryHoursAgo < 1 ? "last seen just now"
    : primaryHoursAgo < 48 ? `confirmed ${primaryHoursAgo}h ago`
    : `last seen ${primaryHoursAgo}h ago`

  const destSlug = DESTINATIONS_DB[destination]?.slug
  const dealHref = locked
    ? '/signup'
    : destSlug
      ? `/destinations/${destSlug}?deal=${id}`
      : `/deals/${id}`

  return (
    <>
      {showCalendar && <DealCalendarModal deal={deal} onClose={() => setShowCalendar(false)} />}
      {showCalendar && <DealCalendarModal deal={deal} onClose={() => setShowCalendar(false)} />}
      <Link href={dealHref} className="block group min-h-full" onClick={() => window.plausible?.('DealCardClick', { props: { route: `${origin}-${destination}` } })}>
      <div className={`relative bg-white rounded-2xl border w-full min-h-full flex flex-col transition-shadow ${priceWatchMatch ? 'shadow-[0_0_0_3px_rgba(251,146,60,0.25),0_4px_12px_rgba(251,146,60,0.15)] border-orange-400 ring-2 ring-orange-300 group-hover:shadow-[0_0_0_3px_rgba(251,146,60,0.35),0_6px_16px_rgba(251,146,60,0.2)]' : deal_type === 'error_fare' ? 'shadow-sm border-red-400 ring-1 ring-red-200 group-hover:shadow-md' : 'shadow-sm border-gray-100 group-hover:shadow-md'}`}>
        <div className="flex flex-col flex-1">
        {/* Destination photo — hidden on destination detail pages (hidePhoto=true) */}
        {!hidePhoto && (
        <div className="relative aspect-[4/3] w-full overflow-hidden rounded-t-2xl">
          {showGradient ? (
            <div
              className="absolute inset-0 flex items-center justify-center gap-2"
              style={{ background: `linear-gradient(135deg, ${gradFrom}, ${gradTo})` }}
            >
              <span className="text-5xl leading-none">{originFlag}</span>
              <span className="text-xl text-white/80 font-bold">→</span>
              <span className="text-5xl leading-none">{destFlag}</span>
            </div>
          ) : destPhotoUrl ? (
            <Image
              src={destPhotoUrl}
              alt=""
              fill
              {...(aboveFold ? { priority: true } : { loading: 'lazy' })}
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw"
              className="object-cover"
            />
          ) : (
            <div
              className="absolute inset-0"
              style={{ background: `linear-gradient(135deg, ${gradFrom}, ${gradTo})` }}
            />
          )}
          {/* Gradient overlay */}
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
          {/* Top-left: deal count (above) + price + time badges */}
          <div className="absolute top-2 left-2 flex flex-col gap-1 items-start">
            {isDealOfHour && <span className="bg-amber-500/90 text-white text-[10px] font-semibold px-2 py-0.5 rounded-lg">⚡ Deal of the hour</span>}
            {(() => { const count = departure_dates?.length || date_count || 1; return <span className="bg-black/60 text-white text-xs font-semibold px-2 py-0.5 rounded-lg">{count} {count === 1 ? 'deal' : 'deals'}</span> })()}
            <span className="bg-black/60 text-white text-xs font-semibold px-2 py-0.5 rounded-lg">
              {isMultiDate ? `from ${photoPriceLabel}` : photoPriceLabel}
            </span>
            {photoTimeLabel && (
              <span className="bg-black/40 text-white/80 text-xs px-2 py-0.5 rounded-lg">
                {photoTimeLabel}
              </span>
            )}
          </div>
          {/* Bottom-left: destination info */}
          <div className="absolute bottom-0 left-0 p-2.5 text-white drop-shadow-sm">
            <div className="font-semibold text-sm leading-tight">{destFlag && `${destFlag} `}{destCity}</div>
            <div className="text-white/70 text-xs">{destCountry}</div>
            <div className="mt-0.5">
              <span className="inline-block bg-white/20 text-white text-xs px-1.5 py-0.5 rounded font-mono leading-none">{destination}</span>
            </div>
          </div>
          {discountPct > 0 ? (
            <span className={`absolute top-2 right-2 text-white text-xs font-bold px-2 py-0.5 rounded-full z-10 ${
              deal_type === 'error_fare' || deal_type === 'exceptional' ? 'bg-red-500' :
              deal_type === 'deal' ? 'bg-orange-500' :
              deal_type === 'good_price' ? 'bg-green-500' :
              'bg-red-500'
            }`}>
              −{discountPct}%
            </span>
          ) : (() => {
            const tier = priceTier(price, totalDuration, deal_category)
            if (!tier) return null
            return (
              <span className={`absolute top-2 right-2 text-white text-xs font-bold px-2 py-0.5 rounded-full z-10 ${tier.bg}`}>
                {tier.label}
              </span>
            )
          })()}
        </div>
        )}

        {/* Banner slot — error fare urgency or destination tagline, always same height */}
        {deal_type === 'error_fare' ? (
          <div className="bg-gradient-to-r from-red-600 to-orange-500 px-4 py-1.5 flex items-center gap-2">
            <span className="text-white text-xs font-bold tracking-wider shrink-0">⚡ ERROR FARE</span>
            <span className="text-white/60 text-xs shrink-0">·</span>
            <ErrorFareUrgency expiresAt={expired_at} />
          </div>
        ) : (
          <div className="px-4 py-1.5 flex items-center bg-gray-50 border-b border-gray-100 min-h-[32px]">
            {(() => {
              const dest = DESTINATIONS_DB[destination]
              const tagline = dest?.itinerary_title
                || (() => {
                  const desc = dest?.description || ''
                  const clip = desc.split(',')[0].replace(/^[A-Za-z]+ is /, '').trim()
                  return clip.length > 10 && clip.length < 60 ? clip : null
                })()
              return tagline
                ? <span className="text-[11px] text-gray-500 font-medium truncate">✦ {tagline}</span>
                : null
            })()}
          </div>
        )}

        <div className="p-5 flex flex-col flex-1">
        {/* Badges row */}
        <div className="flex flex-wrap gap-1 mb-2">
          {priceWatchMatch && (
            <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              🎯 Matches your alert
            </span>
          )}
          {wishlist.length > 0 && wishlist.includes(destination) && (
            <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-0.5 rounded-full">
              ⭐ Wishlist
            </span>
          )}
          {isNew && (
            <span className="bg-slate-700 text-white text-xs font-medium px-2 py-0.5 rounded-full">
              New
            </span>
          )}
          {deal_type !== 'error_fare' && <DealTypeBadge type={type} />}
          <PaymentTypeBadge dealType={deal_type} />
          {isFlexMatch && (
            <span className="bg-blue-100 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full">
              {t("flexWindow")}
            </span>
          )}
          {showSeasonBadge && (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              seasonActive ? 'bg-sky-50 text-sky-600' : 'bg-gray-100 text-gray-500'
            }`}>
              {seasons === 'summer' ? '🌞 Summer' : '❄️ Winter'}
              {!seasonActive && ' · off-season'}
            </span>
          )}
          {hidePhoto && photoTimeLabel && (
            <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded-full">
              {photoTimeLabel}
            </span>
          )}
        </div>
        {/* Trip type */}
        {(() => {
          const isRoundtrip = returnDate || departure_dates?.some(d => d.returnDate)
          return (
            <div className="mb-2 text-xs text-gray-500 font-medium">
              {isRoundtrip ? '↩ Roundtrip' : '→ One-way'}
            </div>
          )
        })()}
        {/* Route - stacked, flag+city on one line per leg */}
        <div>
          <div className="text-base sm:text-xl font-bold text-gray-900 leading-tight truncate">
            {originFlag} {originCity} <span className="text-base font-normal" aria-hidden>🛫</span>
          </div>
          <div className="text-xs text-gray-500 font-normal">{origin} · {originCountry}</div>
          <div className="my-1" />
          <div className="text-base sm:text-xl font-bold text-gray-900 leading-tight truncate">
            {destFlag} {destCity} <span className="text-base font-normal" aria-hidden>🛬</span>
          </div>
          <div className="text-xs text-gray-500 font-normal">
            {DESTINATIONS_DB[destination] ? (
              <Link href={`/destinations/${DESTINATIONS_DB[destination]?.slug ?? destination.toLowerCase()}${homeAirports.length ? `?airports=${homeAirports.join(',')}` : ''}`} onClick={(e) => e.stopPropagation()} className="hover:underline">{destination}</Link>
            ) : (
              <span>{destination}</span>
            )}
            {' · '}{destCountry}
            {type === 'open_jaw' && open_jaw_return_origin && (
              <span className="ml-1 text-teal-500">↩ {open_jaw_return_origin} → {origin}</span>
            )}
          </div>
        </div>
        {/* Price row */}
        <div className="mt-3">
          <div className="flex items-baseline gap-2 flex-wrap">
          {deal_type === 'award' && award_price_miles ? (
            <span className="text-xl font-bold text-amber-700">
              {award_price_miles.toLocaleString()} miles
            </span>
          ) : (() => {
            const { primary, secondary } = formatDealPrice(photoMinPrice);
            return (
              <>
                {isMultiDate && <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mr-0.5">from</span>}
                <span className="text-xl font-bold text-gray-900">{primary}</span>
                {secondary && <span className="text-sm text-gray-500">({secondary})</span>}
                {discountPct >= 10 && normalPrice >= 30 && normalPrice > price && !isLowConfidence && (
                  <span
                    className={`text-xs font-semibold cursor-help ${
                      discountPct >= 50 ? 'text-red-500' :
                      discountPct >= 25 ? 'text-orange-500' :
                      'text-green-600'
                    }`}
                    title={(() => {
                      const depMonth = departureDate?.substring(0, 7)
                      const monthData = depMonth && priceByMonth?.[depMonth]
                      if (monthData && monthData.n >= 2) {
                        const pctVsMedian = Math.round((1 - price / monthData.median) * 100)
                        return `${pctVsMedian}% below typical price in ${new Date(depMonth + '-01').toLocaleString('en', {month: 'long'})} (median €${Math.round(monthData.median)} based on ${monthData.n} dates)`
                      }
                      if (discount_source === 'google_typical' && typicalPriceRange?.length === 2) {
                        return `${Math.round((1 - price / typicalPriceRange[0]) * 100)}% below Google Flights typical low price (€${Math.round(typicalPriceRange[0])})`
                      }
                      return `${discountPct}% below typical price for this route (€${Math.round(normalPrice)})`
                    })()}
                  >−{discountPct}%</span>
                )}
              </>
            );
          })()}
          </div>
          {(() => {
            const hasRange = typicalPriceRange?.length === 2
            const showAvg = !hasRange && normalPrice >= 30 && normalPrice > price && !isLowConfidence
            const hasInfo = hasRange || showAvg
            return (
              <div className={`text-xs mt-1 leading-snug min-h-[2.5rem]${!hasInfo ? ' invisible' : ''}`}>
                {hasRange && (
                  <span className="text-gray-500">
                    <span className="block">Google Flights typical:</span>
                    <span className="block font-medium text-gray-500">{formatDealPrice(typicalPriceRange[0]).primary}–{formatDealPrice(typicalPriceRange[1]).primary}</span>
                  </span>
                )}
                {!hasRange && showAvg && (
                  <span className="text-gray-500">
                    <span className="block">Typically costs:</span>
                    <span className="block font-medium text-gray-500">{formatDealPrice(normalPrice).primary}</span>
                  </span>
                )}
              </div>
            )
          })()}

        </div>
        {(() => {
          const months = departure_months?.length > 0
            ? departure_months
            : departureDate ? [parseInt(departureDate.split('-')[1], 10)] : []
          const dates = departure_dates?.length > 0
            ? departure_dates
            : departureDate ? [{ date: departureDate, returnDate: returnDate ?? null, price, id, airline: airline ?? null, stops: stops ?? null }] : []
          return months.length > 0 ? (
            <>
              <MonthBar departure_months={months} departure_dates={dates} formatDealPrice={formatDealPrice} currentUser={currentUser} tier={resolvedTier} airlineLogo={airlineLogo} carryOnIncluded={carryOnIncluded} destSlug={destSlug} legs={legs} layovers={layovers} />
              {currentUser ? (
                <Link
                  href={`/app/calendar?dest=${destination}`}
                  className="mt-0.5 block w-full text-[10px] text-gray-500 hover:text-blue-500 hover:underline"
                  onClick={e => e.stopPropagation()}
                >
                  See all prices in calendar →
                </Link>
              ) : (
                <Link
                  href={`/flights/${routeSlug(origin, destination)}`}
                  className="mt-0.5 block w-full text-[10px] text-gray-500 hover:text-blue-500 hover:underline"
                  onClick={e => e.stopPropagation()}
                >
                  See all prices in calendar →
                </Link>
              )}
            </>
          ) : null
        })()}

        {/* Price history sparkline — data fetched in batch by DealsShell */}
        <PriceSparkline prices={sparklinePrices} locked={false} />


        {/* Deal quality badge — space always reserved so cards in a grid align */}
        <div className={isLowConfidence ? 'invisible' : ''}>
          <DealQualityBadge discountPct={discountPct} deal_type={deal_type} normalPrice={normalPrice} price={price} formatDealPrice={formatDealPrice} tier={tier} />
        </div>



        {/* Baggage / interline warning */}
        <BaggageWarning interline={interline_warning} baggage={!interline_warning && baggage_warning} />

        <div className="mt-auto">
        {/* Expiry warning (not shown for error fares — banner handles urgency) */}
        {expired_at && deal_type !== 'error_fare' && (
          <div className="mt-1 text-xs text-amber-600 font-medium">
            ⚠ Last seen {expiredHoursAgo < 1 ? 'less than 1h ago' : `${expiredHoursAgo}h ago`}
          </div>
        )}




        {/* CO₂ estimate */}
        {co2_kg && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
            <span>🌱</span>
            <span>~{co2_kg >= 1000 ? `${(co2_kg / 1000).toFixed(1)}t` : `${co2_kg}kg`} CO₂e return</span>
            <span>·</span>
            <a
              href="https://www.atmosfair.de/en/offset/flight/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => { e.stopPropagation(); window.plausible?.('CO2OffsetClick', { props: { route: `${origin}-${destination}` } }) }}
              className="text-green-600 hover:underline"
            >
              Offset →
            </a>
          </div>
        )}

        {/* CTAs */}
        <div className="mt-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 min-w-0">
            <button
              onClick={handleShareClick}
              aria-label="Share deal"
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors px-2 py-2 rounded-lg hover:bg-gray-100 shrink-0"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
              </svg>
              <span className="hidden sm:inline">Share</span>
            </button>
            {currentUser && !locked && (
              reportState === 'expired' ? (
                <span className="text-xs text-amber-600 font-medium">⚠ Mogelijk verlopen</span>
              ) : reportState === 'still_valid' ? (
                <span className="text-xs text-green-600 font-medium">✓ Deal bestaat nog</span>
              ) : (
                <button
                  onClick={handleReportExpired}
                  disabled={reportState === 'loading'}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-amber-600 transition-colors px-2 py-1 rounded-lg hover:bg-amber-50 disabled:opacity-50"
                >
                  {reportState === 'loading' ? '…' : '⚠ Verlopen?'}
                </button>
              )
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {!showGoogleFlights && (() => {
              const durSlug = travelDuration ? (DURATION_TO_SLUG[travelDuration] || null) : null
              const internalHref = durSlug ? `/deals/${dealSlug(origin, destination, travelDuration)}` : null
              return internalHref ? (
                <a
                  href={internalHref}
                  onClick={(e) => { e.stopPropagation(); trackClick({ dealId: id, origin, destination, price, discountPct, tier }) }}
                  className="flex items-center gap-1 text-xs bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 rounded-lg transition-colors font-medium whitespace-nowrap"
                >
                  View Deal →
                </a>
              ) : (
                <span className="flex items-center gap-1 text-xs bg-blue-600 group-hover:bg-blue-700 text-white px-2.5 py-1 rounded-lg transition-colors font-medium whitespace-nowrap">
                  View Deal →
                </span>
              )
            })()}
          </div>
        </div>

        {/* Booking options — shown when deal is unlocked */}
        {showGoogleFlights && (() => {
          const ab = buildAirlineBookingUrl(airlineCode, origin, destination, departureDate, returnDate)
          const sk = buildSkyscannerUrl(origin, destination, departureDate, returnDate, airlineCode)
          const ky = buildKayakUrl(origin, destination, departureDate, returnDate)
          const mo = buildMomondoUrl(origin, destination, departureDate, returnDate)
          const kw = buildKiwiUrl(origin, destination, departureDate, returnDate)
          const tr = buildTripUrl(origin, destination, departureDate, returnDate)
          const gfUrl = buildGoogleFlightsUrl(origin, destination, departureDate, returnDate, stops, currency)
          const row = (href, logo, label, event, isPrimary = false) => (
            <a href={href} target="_blank" rel="noopener"
              onClick={(e) => { e.stopPropagation(); trackEvent(event, { dealId: id, origin, destination }, tier) }}
              className={`flex items-center gap-2.5 w-full px-3 py-2 rounded-lg transition-colors group ${isPrimary ? 'hover:bg-blue-50' : 'hover:bg-gray-50'}`}>
              <img src={logo} alt="" className="w-5 h-5 rounded object-contain shrink-0" />
              <span className={`text-sm ${isPrimary ? 'font-semibold text-gray-800' : 'text-gray-600'}`}>{label}</span>
              <span className="ml-auto text-gray-500 group-hover:text-gray-600 text-xs">→</span>
            </a>
          )
          return (
            <>
            <p className="mt-3 mb-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide">Book this flight via</p>
            <div className="border border-gray-100 rounded-xl overflow-hidden divide-y divide-gray-100">
              {ab && row(ab.url, airlineLogo || `https://www.google.com/s2/favicons?domain=skyscanner.com&sz=32`, `Book on ${ab.name}`, 'airline_direct_click', true)}
              {sk && row(sk, 'https://www.google.com/s2/favicons?domain=skyscanner.com&sz=32', 'Skyscanner', 'skyscanner_click')}
              {!sk && tr && row(tr, 'https://www.google.com/s2/favicons?domain=trip.com&sz=32', 'Trip.com', 'trip_click')}
              {ky && row(ky, 'https://www.google.com/s2/favicons?domain=kayak.com&sz=32', 'Kayak', 'kayak_click')}
              {mo && row(mo, 'https://www.google.com/s2/favicons?domain=momondo.com&sz=32', 'Momondo', 'momondo_click')}
              {kw && row(kw, 'https://www.google.com/s2/favicons?domain=kiwi.com&sz=32', 'Kiwi.com', 'kiwi_click')}
              <a href={gfUrl} target="_blank" rel="noopener"
                onClick={(e) => { e.stopPropagation(); trackEvent('google_flights_click', { dealId: id, origin, destination, price, discountPct }, tier) }}
                className="flex items-center gap-2.5 w-full px-3 py-2 hover:bg-gray-50 transition-colors group">
                <img src="https://www.google.com/s2/favicons?domain=google.com&sz=32" alt="" className="w-5 h-5 rounded shrink-0 opacity-60" />
                <span className="text-xs text-gray-500">Verify price on Google Flights</span>
                <span className="ml-auto text-gray-200 group-hover:text-gray-300 text-xs">→</span>
              </a>
            </div>
            </>
          )
        })()}

        {/* Share menu — shown after clicking Share when Web Share API unavailable */}
        {shareOpen && (
          <div className="mt-2" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
            <ShareMenu
              compact
              dealUrl={`${window.location.origin}/deals/${id}`}
              shareText={`Check this flight deal! ${originCity} → ${destCity} for ${currencySymbol(deal.currency)}${price} on FairFares`}
              onClose={() => setShareOpen(false)}
              refCode={refCode}
            />
          </div>
        )}
        </div>{/* end mt-auto bottom block */}
        </div>{/* end p-5 content */}
        </div>{/* end pointer-events-none wrapper */}

        {locked && (
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-10 rounded-2xl">
            <span className="text-white text-sm font-bold bg-black/30 border border-white/30 shadow-lg px-5 py-2.5 rounded-full">{deal._proOnly ? '✈️ Pro — Upgrade →' : '🔒 Unlock all deals →'}</span>
          </div>
        )}
      </div>
    </Link>
    </>
  );
}
